<template>
  <div class="depts-page py-3">
    <header class="depts-toolbar">
      <div class="depts-toolbar__title">
        <span class="depts-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Departments</h1>
          <p>
            <span class="depts-toolbar__org">{{ activeOrganisationName || 'the current organisation' }}</span>
            · Switch organisation in the sidebar to manage another
          </p>
        </div>
      </div>
      <div class="depts-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="depts-updated">
          Updated {{ lastUpdatedLabel }}
        </span>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-dept-btn"
          @click="goToCreate"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-dept-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Create Department</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="depts-alert">{{ errorMessage }}</CAlert>

    <div class="summary-tiles">
      <div class="summary-tile summary-tile--scope">
        <span class="summary-tile__label">Scope</span>
        <span class="summary-tile__value summary-tile__value--text">{{ activeOrganisationName || '—' }}</span>
      </div>
      <div class="summary-tile summary-tile--total">
        <span class="summary-tile__label">Total Departments</span>
        <span class="summary-tile__value">{{ departments.length }}</span>
      </div>
      <div class="summary-tile summary-tile--users">
        <span class="summary-tile__label">Users Assigned</span>
        <span class="summary-tile__value">{{ totalUsersAssigned }}</span>
      </div>
      <div class="summary-tile summary-tile--largest">
        <span class="summary-tile__label">Largest Department</span>
        <span class="summary-tile__value summary-tile__value--text">{{ largestDepartmentLabel }}</span>
      </div>
    </div>

    <BaseTable
      :items="filteredDepartments"
      :columns="columns"
      title="All Departments"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      :default-sort="{ column: 'name', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      class="depts-table"
    >
      <template #toolbar>
        <div class="depts-search-wrap">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="depts-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="depts-input"
            placeholder="Search departments…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="depts-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>
      </template>

      <template #cell-name="{ row }">
        <div class="dept-name">
          <span class="dept-marker" :style="markerStyle(row.name)" aria-hidden="true"></span>
          <span class="dept-name__text">{{ row.name }}</span>
        </div>
      </template>

      <template #cell-userCount="{ row }">
        <span class="dept-user-count">
          <span class="dept-user-count__value">{{ row.userCount }}</span>
          <span class="dept-user-count__label">{{ row.userCount === 1 ? 'user' : 'users' }}</span>
        </span>
      </template>

      <template #cell-share="{ row }">
        <div class="dept-share">
          <div class="dept-share__bar">
            <div
              class="dept-share__fill"
              :style="shareBarStyle(row)"
            ></div>
          </div>
          <span class="dept-share__pct">{{ sharePercent(row) }}</span>
        </div>
      </template>

      <template #cell-actions="{ row }">
        <button type="button" class="edit-btn" @click="goToEdit(row.id)">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span>Edit</span>
        </button>
      </template>

      <template #empty>
        <div v-if="!departments.length" class="depts-empty">
          <div class="depts-empty__title">No departments yet</div>
          <div class="depts-empty__hint">
            <button type="button" class="depts-empty__link" @click="goToCreate">Create your first department</button>
            for {{ activeOrganisationName || 'this organisation' }}.
          </div>
        </div>
        <div v-else class="depts-empty">
          <div class="depts-empty__title">No matches for "{{ searchTerm }}"</div>
          <div class="depts-empty__hint">
            Try a different name, or
            <button type="button" class="depts-empty__link" @click="searchTerm = ''">clear search</button>.
          </div>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'

const MARKER_PALETTE = ['#0e5b46', '#1e40af', '#5b21b6', '#92400e', '#115e59', '#7f1d1d']

export default {
  name: 'SettingsDepartments',
  components: { BaseTable },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      departments: [],
      lastUpdatedAt: null,
      columns: [
        { key: 'id', label: 'Id', width: '64px', align: 'right' },
        { key: 'name', label: 'Department' },
        { key: 'userCount', label: 'Users', width: '120px', align: 'right' },
        { key: 'share', label: 'Share of users', width: '220px' },
        { key: 'actions', label: '', width: '88px', align: 'right', sortable: false }
      ]
    }
  },
  computed: {
    activeOrganisationName () {
      return this.$store?.state?.activeOrganisationName || ''
    },
    activeOrganisationId () {
      return this.$store?.state?.activeOrganisationId || null
    },
    filteredDepartments () {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.departments
      return this.departments.filter((dept) =>
        [dept.id, dept.name]
          .filter((value) => value !== null && value !== undefined && value !== '')
          .some((value) => String(value).toLowerCase().includes(term))
      )
    },
    totalUsersAssigned () {
      return this.departments.reduce((sum, dept) => sum + (dept.userCount || 0), 0)
    },
    largestDepartmentLabel () {
      if (!this.departments.length) return '—'
      const top = [...this.departments].sort((a, b) => (b.userCount || 0) - (a.userCount || 0))[0]
      if (!top || !top.userCount) return '—'
      return `${top.name} · ${top.userCount}`
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.isLoading = true
      this.errorMessage = ''

      const token = localStorage.getItem('api_token')
      const deptPromise = axios.get(`${this.$apiAdress}/api/department?token=${token}`)
      const usersPromise = axios.get(`${this.$apiAdress}/api/users?token=${token}`)

      Promise.all([deptPromise, usersPromise])
        .then(([deptRes, usersRes]) => {
          const depts = this.extractDepartments(deptRes.data)
          const users = this.extractUsers(usersRes.data)
          const counts = this.countUsersPerDepartment(users)

          this.departments = depts.map((dept) => ({
            ...dept,
            userCount: counts[String(dept.id)] || counts[dept.name] || 0
          }))
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load departments right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    extractDepartments (data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      if (Array.isArray(data?.departments)) return data.departments
      return []
    },
    extractUsers (data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.users)) return data.users
      if (Array.isArray(data?.data)) return data.data
      return []
    },
    countUsersPerDepartment (users) {
      const counts = {}
      users.forEach((user) => {
        const id = user?.department_id ?? user?.department?.id
        if (id !== null && id !== undefined && id !== '') {
          const key = String(id)
          counts[key] = (counts[key] || 0) + 1
        }
        const name = typeof user?.department === 'string'
          ? user.department
          : (user?.department?.name || user?.department_name || '')
        if (name) {
          counts[name] = (counts[name] || 0) + 1
        }
      })
      return counts
    },
    sharePercent (row) {
      const total = this.totalUsersAssigned
      if (!total) return '0%'
      const pct = ((row.userCount || 0) / total) * 100
      return `${pct < 1 && pct > 0 ? '<1' : Math.round(pct)}%`
    },
    shareBarStyle (row) {
      const total = this.totalUsersAssigned
      const pct = total ? ((row.userCount || 0) / total) * 100 : 0
      const clamped = Math.max(0, Math.min(100, pct))
      return {
        width: `${clamped}%`,
        background: this.markerStyle(row.name).background
      }
    },
    markerStyle (name) {
      const text = String(name || '')
      let hash = 0
      for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) >>> 0
      }
      return { background: MARKER_PALETTE[hash % MARKER_PALETTE.length] }
    },
    goToCreate () {
      this.$router.push('/settings/departments/create')
    },
    goToEdit (id) {
      this.$router.push(`/settings/departments/${id}/edit`)
    }
  }
}
</script>

<style scoped>
.depts-page {
  padding: 0.25rem 0;
}

.depts-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.depts-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
}

.depts-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.depts-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.depts-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.depts-toolbar__org {
  color: #0e5b46;
  font-weight: 700;
}

.depts-toolbar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-left: auto;
  flex-shrink: 0;
}

.depts-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.depts-toolbar__actions >>> .btn.create-dept-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.depts-toolbar__actions >>> .btn.create-dept-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.depts-toolbar__actions >>> .btn.create-dept-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-dept-btn__icon {
  flex-shrink: 0;
  display: block;
}

.depts-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.depts-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.depts-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.depts-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.depts-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

.depts-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.summary-tiles {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
  min-width: 0;
}

.summary-tile--scope   { border-left-color: #16a07a; }
.summary-tile--total   { border-left-color: #1f2a44; }
.summary-tile--users   { border-left-color: #6d28d9; }
.summary-tile--largest { border-left-color: #b45309; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

.summary-tile__value--text {
  font-size: 0.95rem;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.depts-table {
  margin-bottom: 1rem;
}

.depts-search-wrap {
  position: relative;
  width: 280px;
  max-width: 100%;
}

.depts-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.depts-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.depts-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.depts-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.depts-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.depts-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.depts-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.depts-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.depts-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.depts-empty__link:hover {
  text-decoration: underline;
}

.dept-name {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.dept-marker {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dept-name__text {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.dept-user-count {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  justify-content: flex-end;
}

.dept-user-count__value {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}

.dept-user-count__label {
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 500;
}

.dept-share {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.dept-share__bar {
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.dept-share__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.dept-share__pct {
  font-size: 0.74rem;
  font-weight: 700;
  color: #4b5563;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
  text-align: right;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.edit-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

@media (max-width: 1199px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .depts-toolbar {
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .depts-toolbar__title {
    flex: 1 1 100%;
  }
  .depts-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
  .summary-tiles {
    grid-template-columns: 1fr;
  }
  .depts-search-wrap {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .depts-page {
    padding: 0 0 0.5rem;
  }
  .depts-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .depts-toolbar__accent {
    height: 30px;
  }
  .depts-toolbar__title h1 {
    font-size: 1rem;
  }
  .depts-toolbar__title p {
    font-size: 0.72rem;
  }
  .depts-toolbar__actions {
    gap: 0.45rem;
  }
  .depts-toolbar__actions >>> .btn.create-dept-btn,
  .depts-toolbar__actions >>> .btn.refresh-btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .depts-updated {
    flex: 1 1 100%;
    font-size: 0.7rem;
  }
  .summary-tile {
    padding: 0.65rem 0.8rem;
  }
  .summary-tile__value {
    font-size: 1.1rem;
  }
}
</style>
